import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Modal, Form, Button, Popover, Radio, Icon, Switch } from 'antd';
import PropTypes from 'prop-types';
const { Item } = Form;

const CustomForm = Form.create({
	name: 'config_modal_form',
	onFieldsChange(props, changedFields) {
		props.onChange(changedFields);
	},
	mapPropsToFields(props) {
		return props.items.reduce((o, item) => ({ ...o, [item.id]: Form.createFormField(item) }), {});
	},
})(
	forwardRef((props, ref) => {
		const { getFieldDecorator, validateFields } = props.form;

		useImperativeHandle(ref, () => ({
			validateFields,
		}));

		const style = {
			labelCol: { xs: { span: 24 }, sm: { span: 8 } },
			wrapperCol: { xs: { span: 24 }, sm: { span: 16 } },
		};

		return (
			<Form>
				{props.items.map((item) => (
					<Item {...style} key={item.id} label={item.label}>
						{getFieldDecorator(item.id, item.decorator)(item.component)}
					</Item>
				))}
			</Form>
		);
	})
);

function ConfigModal({
	open,
	formItems,
	onSubmit,
	onDelete,
	onCancel,
	createNew,
	title,
	values,
	multiForm = [],
	multiDefault = null,
	onMultiFormChange,
	disableMask,
}) {
	const [showModal, setShowModal] = useState(false);
	const [showConfirmDelete, setShowConfirmDelete] = useState(false);
	const [formFields, setFormFields] = useState([]);
	const [selectedMultiForm, setSelectedMultiForm] = useState(0);
	const formRef = useRef(null);

	useEffect(() => {
		setShowModal(open);

		return () => {
			setShowModal(false);
		};
	}, [open]);

	useEffect(() => {
		if (multiDefault !== null) {
			const newIndex = multiForm.indexOf(multiDefault);

			if (newIndex >= 0) {
				handleMultiFormChange(newIndex);
			}
		}
	}, [multiDefault]);

	useEffect(() => {
		const formFieldItems =
			multiForm && multiForm.length > 0 ? formItems[selectedMultiForm] : formItems;

		if (values && Object.keys(values).length > 0) {
			const newFields = formFieldItems.map((field) => {
				if (values[field.id] !== undefined) {
					return {
						...field,
						value: values[field.id],
						decorator: {
							...field.decorator,
							initialValue: values[field.id],
						},
					};
				}

				return field;
			});

			setFormFields(newFields);
		} else {
			setFormFields(
				formFieldItems.map((item) => ({ ...item, value: item.decorator.initialValue || undefined }))
			);
		}

		return () => {
			setFormFields([]);
		};
	}, [values, formItems, selectedMultiForm]);

	const handleSubmit = (e) => {
		e.preventDefault();

		formRef.current.validateFields((err, val) => {
			if (!err) {
				onSubmit(formFields.reduce((o, item) => ({ ...o, [item.id]: item.value }), {}));
				setShowModal(false);

				if (onCancel) {
					onCancel();
				}
			}
		});
	};

	const handleFormChange = (changedFields) => {
		const newFields = formFields.map((field) => {
			if (changedFields[field.id]) {
				return {
					...field,
					...changedFields[field.id],
				};
			}

			return field;
		});

		setFormFields(newFields);
	};

	const handleMultiFormChange = (key) => {
		setSelectedMultiForm(key);

		if (onMultiFormChange) {
			onMultiFormChange(key);
		}
	};

	let footer = [
		<Button
			key='back'
			onClick={() => {
				setShowModal(false);
				if (onCancel) {
					onCancel();
				}
			}}
		>
			Avbryt
		</Button>,
		<Button key='submit' type='primary' onClick={handleSubmit}>
			{createNew ? 'Skapa' : 'Spara'}
		</Button>,
	];

	if (!createNew && onDelete) {
		footer.push(
			<div key='confirm-delete' style={{ float: 'left' }}>
				<Popover
					visible={showConfirmDelete}
					placement='topLeft'
					title={'Är du säker?'}
					content={
						<div>
							<Button
								type='danger'
								onClick={() => {
									onDelete();
									setShowConfirmDelete(false);
								}}
							>
								Ja
							</Button>
							<Button style={{ marginLeft: 10 }} onClick={() => setShowConfirmDelete(false)}>
								Nej
							</Button>
						</div>
					}
					trigger='click'
				>
					<Button type='danger' onClick={() => setShowConfirmDelete(true)}>
						<Icon type='delete' /> Radera
					</Button>
				</Popover>
			</div>
		);
	}

	return (
		<Modal
			title={title}
			visible={showModal}
			width={800}
			onCancel={() => {
				setShowModal(false);
				if (onCancel) {
					onCancel();
				}
			}}
			maskClosable={!disableMask}
			footer={footer}
		>
			{multiForm && (
				<div style={{ display: 'flex', justifyContent: 'center' }}>
					<Radio.Group
						value={selectedMultiForm}
						size='default'
						buttonStyle='solid'
						onChange={(e) => handleMultiFormChange(e.target.value)}
						style={{
							marginBottom: 16,
						}}
					>
						{multiForm.map((item, index) => (
							<Radio.Button key={index} value={index}>
								{item}
							</Radio.Button>
						))}
					</Radio.Group>
				</div>
			)}
			{open && (
				<CustomForm
					ref={formRef}
					items={formFields}
					key={selectedMultiForm}
					onChange={handleFormChange}
				/>
			)}
		</Modal>
	);
}

ConfigModal.propTypes = {
	open: PropTypes.bool,
	formItems: PropTypes.array,
	onSubmit: PropTypes.func,
	onDelete: PropTypes.func,
	onCancel: PropTypes.func,
	multiForm: PropTypes.array,
	title: PropTypes.string,
	onMultiFormChange: PropTypes.func,
	disableMask: PropTypes.bool,
};

ConfigModal.defaultProps = {
	disableMask: false,
};

export { ConfigModal, ConfigModal as default };



// WEBPACK FOOTER //
// ./src/components/ConfigModal.jsx